import axios from 'axios'
import React, { useState, useCallback, useContext } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Collapse, Paper,
  Tooltip
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { getRequestConfig } from '../auth/auth.js'
import { ApiGateway, FeatureFlags } from '../config/config.js'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Speed, Calculate, Grade } from '@mui/icons-material'

function CalculationAuditTable({ rows }) {
  return (
    <TableContainer component={Paper}>
      <Table
        size="small"
        aria-label="calculation audit"
        sx={{ tableLayout: 'fixed', width: '100%' }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '5%' }}></TableCell>
            <TableCell sx={{ width: '15%' }}>Metric/Score</TableCell>
            <TableCell sx={{ width: '15%' }}>Value</TableCell>
            <TableCell sx={{ width: '65%' }}>Formula</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, idx) => (
            <Row key={idx} {...row} />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Row({ label, value, penalty, score, formula, valuesFormula, highlighted, auditType }) {
  const [open, setOpen] = useState(false);
  const [showValues, setShowValues] = useState(false);
  const isScore = score !== undefined;
  const isPenalty = penalty !== undefined;

  return (
    <Tooltip
      title={highlighted ? 
        auditType === 'metric'
          ? "This calculation was affected by the audited metric"
          : "This calculation contributes to the audited score"
        : ""}
      placement="top-end"
      arrow
    >
      <TableRow
        sx={{
          backgroundColor: highlighted ? 'rgba(255, 255, 0, 0.2)' : 'inherit'
        }}
      >
        <TableCell>
          {isScore ? (
            <Grade title="Calculated Score" />
          ) : isPenalty ? (
            <Calculate title="Calculated Penalty" />
          ) : (
            <Speed title="Metric" />
          )}
        </TableCell>
        <TableCell>{label}</TableCell>
        <TableCell
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: isPenalty ? 'red' : 'black'
          }}
        >
          {isScore ? score : isPenalty ? penalty : value}
        </TableCell>
        <TableCell sx={{ verticalAlign: 'top' }}>
          {formula && (
            <>
              <IconButton size="small" onClick={() => setOpen(!open)}>
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
              <Collapse in={open} timeout="auto" unmountOnExit>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    mb: 1
                  }}
                >
                  {valuesFormula && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setShowValues(!showValues)}
                    >
                      {showValues ? 'Show Metric Labels' : 'Show Metric Values'}
                    </Button>
                  )}
                </Box>
                <Box
                  component="pre"
                  sx={{
                    backgroundColor: '#f4f4f4',
                    p: 1,
                    borderRadius: 1,
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                    maxHeight: 200,
                    overflowY: 'auto',
                    overflowX: 'auto'
                  }}
                >
                  {showValues && valuesFormula ? valuesFormula : formula}
                </Box>
              </Collapse>
            </>
          )}
        </TableCell>
      </TableRow>
    </Tooltip>
  );
}

function hasDescendantMetricId(node, targetMetricId) {
  if (!node?.children) return false;

  return node.children.some(child => {
    // Direct match
    if (child.metricId === targetMetricId) {
      return true;
    }
    // Recursively search deeper
    return hasDescendantMetricId(child, targetMetricId);
  });
}


export function CalculationAudit({ metricId, scoreId, nodes, icon, children, cacheContext }) {
  const [open, setOpen] = useState(false);
  const [auditRows, setAuditRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAudit = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const path = metricId
        ? `${ApiGateway.fitnessIndexes}/audit/metric/${metricId}?feature-flag-calculator=${FeatureFlags.calculator}`
        : `${ApiGateway.fitnessIndexes}/audit/score/${scoreId}?feature-flag-calculator=${FeatureFlags.calculator}`;
      
      const cacheKey = `calc-audit-${metricId || scoreId}`
      const cacheHit = cacheContext.getItem(cacheKey)

      if(cacheHit) {
        setAuditRows(cacheHit)
      } else {
        const requestConfig = await getRequestConfig();
        const { data } = await axios.post(path, nodes, requestConfig);

        // Expecting { auditResults: { scoreNode: { children: [...] }, trace: [...] } }
        const auditResults = data?.data?.auditResults;
        const trace = data?.data?.trace;
        const targetNode = metricId ? auditResults?.metricNode : auditResults?.scoreNode
        const highlightNodes = metricId
          ? auditResults?.scoreNodes
          : targetNode.children
        if (trace.length) {
          const contributingLabels = new Set(
            highlightNodes?.map(c => c.label)
          );

          const processedRows = trace.map(row => {
            const highlightNode = highlightNodes.find(f => f.label === row.label);

            const highlighted = metricId
              ? hasDescendantMetricId(highlightNode, metricId)
              : contributingLabels.has(row.label);

            return {
              ...row,
              auditType: metricId ? 'metric' : 'score',
              highlighted
            };
          });
          cacheContext.setItem(cacheKey, processedRows)
          setAuditRows(processedRows);
        } else {
          cacheContext.setItem(cacheKey, [])
          setAuditRows([]);
        }        
      }

    } catch (err) {
      if (err.response) {
        setError(`Server error: ${err.response.status} ${err.response.statusText}`);
      } else if (err.request) {
        setError('Network Error: No response received');
      } else {
        setError(err.message);
      }
    } finally {
      setLoading(false);
    }
  }, [metricId, scoreId, nodes, cacheContext]);

  const handleOpen = () => {
    setOpen(true);
    fetchAudit();
  };

  const handleClose = () => {
    setOpen(false);
  };

  const AuditDialog = (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="95vw"
      fullWidth
    >
      <DialogTitle>
        Metric/Score Audit ({nodes?.[metricId]?.label || nodes?.[scoreId]?.label})
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ backgroundColor: '#fafafa', maxHeight: '60vh', overflowY: 'auto' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, float: 'right' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Grade fontSize="small" color="action" />
            <Typography variant="body2">Calculated Score</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Calculate fontSize="small" color="action" />
            <Typography variant="body2">Calculated Penalty</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Speed fontSize="small" color="action" />
            <Typography variant="body2">Base Fitness Index Metric</Typography>
          </Box>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', p: 2 }}>
            <CircularProgress size={20} sx={{ mr: 1 }} /> Loading...
          </Box>
        )}

        {error && <Typography color="error">{error}</Typography>}

        {!loading && !error && auditRows && auditRows.length > 0 && (
          <CalculationAuditTable rows={auditRows} />
        )}

        {!loading && !error && (!auditRows || auditRows.length === 0) && (
          <Typography>No data available</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (icon) {
    return (
      <div style={{ float: 'left' }}>
        <IconButton size="small" sx={{ ml: 1 }} onClick={handleOpen}>
          <InfoOutlinedIcon fontSize="inherit" />
        </IconButton>
        {AuditDialog}
      </div>
    );
  }

  return (
    <>
      <Box
        onClick={handleOpen}
        sx={{
          cursor: 'pointer',
          transition: 'transform 0.15s ease',
          '&:hover': {
            transform: 'scale(1.02)'
          }
        }}
      >
        {children}
      </Box>
      {AuditDialog}
    </>
  );
}